import { Popup } from 'components/Popup'
import { Typography } from 'components/Typography'
import { useBankingSetupPopup } from 'features/MyCommunity/BankingSetupCommunity/Providers/BankingSetupPopupProvider'
import { BUTTON_TYPES, Button } from 'components/Button'
import { BTN_TXT, LABELS, NOTIFICATIONS, PLACEHOLDERS } from 'constants/txt'
import { useCallback, useMemo, useState } from 'react'
import { Form, Radio, RadioChangeEvent, Space, notification } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import {
  validateMessages,
  formatPhoneNumber,
  formatPhoneNumberToRequest
} from 'helper/common'
import Input from 'components/Input/Input'
import {
  TGenerateResolutionLinkForm,
  TGenerateResolutionLinkFormErrors
} from './types'
import { RESOLUTION_LINK_CONTACT_METHOD } from './constants'
import './styles.scss'
import { generateResolutionLinkRequest } from './api'
import { useDispatch } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'

export const GenerateResolutionLinkPopup = ({
  successCallback
}: {
  successCallback: () => void
}) => {
  const {
    generateResolutionLinkPopup: { state, actions },
    resolutionLinkGeneratedPopup
  } = useBankingSetupPopup()

  const { uuid } = state.payload

  const [form] = Form.useForm<TGenerateResolutionLinkForm>()
  const [errors, setErrors] = useState<TGenerateResolutionLinkFormErrors>({})

  const dispatch = useDispatch()

  const contactMethod = Form.useWatch('contact_method', form)
  const phoneNumber = Form.useWatch('phone_number', form)
  const email = Form.useWatch('email', form)

  const contactValue = useMemo(() => {
    if (contactMethod === RESOLUTION_LINK_CONTACT_METHOD.PHONE) {
      return phoneNumber
    }
    return email
  }, [contactMethod, email, phoneNumber])

  const onCancel = useCallback(() => {
    actions.close()
    setErrors({})
    form.resetFields()
  }, [actions, form])

  const onContactMethodChange = useCallback(
    (event: RadioChangeEvent) => {
      form.setFieldsValue({
        contact_method: event.target.value,
        phone_number: undefined,
        email: undefined
      })
      setErrors({})
    },
    [form]
  )

  const onGenerateResolutionLink = useCallback(async () => {
    if (!uuid) return
    try {
      dispatch(setLoading(true))
      const response = await generateResolutionLinkRequest(
        uuid,
        contactMethod === RESOLUTION_LINK_CONTACT_METHOD.PHONE
          ? {
              phone_number: formatPhoneNumberToRequest(phoneNumber) ?? undefined
            }
          : { email: email }
      )
      actions.close()
      resolutionLinkGeneratedPopup.actions.open({
        link: response.data.url,
        expires_on: response.data.expires_on
      })
    } catch (error) {
      console.error(error)
      notification.error({
        message: NOTIFICATIONS.GENERATE_RESOLUTION_LINK_ERROR
      })
    } finally {
      dispatch(setLoading(false))
      successCallback()
    }
  }, [
    actions,
    contactMethod,
    dispatch,
    email,
    phoneNumber,
    resolutionLinkGeneratedPopup.actions,
    uuid,
    successCallback
  ])

  return (
    <Popup
      {...state}
      width={442}
      zIndex={10000}
      className="generate-resolution-link-popup"
      onCancel={onCancel}
      footer={
        <Button
          type={BUTTON_TYPES.PRIMARY}
          upperCase
          form="generate-resolution-link-form"
          htmlType="submit"
          disabled={!contactValue}
        >
          {BTN_TXT.GENERATE_RESOLUTION_LINK}
        </Button>
      }
    >
      <div className="column gap-16">
        <Typography.Body1>
          Enter your email or phone number. We'll verify your details and send a
          secure link to finish your setup.
        </Typography.Body1>
        <CustomForm<TGenerateResolutionLinkForm>
          form={form}
          id="generate-resolution-link-form"
          layout="vertical"
          onFinish={onGenerateResolutionLink}
          validateMessages={validateMessages}
          initialValues={{
            contact_method: RESOLUTION_LINK_CONTACT_METHOD.EMAIL
          }}
        >
          <div className="column gap-16">
            <Form.Item
              name="contact_method"
              colon={false}
              className="mb-0 generate-resolution-link-popup__radio-group"
            >
              <Radio.Group
                className="generate-resolution-link-popup__contact-method"
                onChange={onContactMethodChange}
              >
                <Space size={24}>
                  <Radio value={RESOLUTION_LINK_CONTACT_METHOD.EMAIL}>
                    {LABELS.EMAIL}
                  </Radio>
                  <Radio value={RESOLUTION_LINK_CONTACT_METHOD.PHONE}>
                    {LABELS.PHONE_NUMBER}
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            {contactMethod === RESOLUTION_LINK_CONTACT_METHOD.PHONE ? (
              <Input.Phone
                propsItem={{
                  name: 'phone_number',
                  label: LABELS.PHONE_NUMBER,
                  help: errors.phone_number?.[0],
                  validateStatus: errors.phone_number?.[0] && 'error',
                  validateTrigger: 'onChange',
                  rules: [
                    {
                      required: true,
                      pattern: /^[0-9]{10}$/
                    }
                  ]
                }}
                propsInputNumber={{
                  prefix: contactValue ? '+1' : '',
                  controls: false,
                  formatter: formatPhoneNumber,
                  placeholder: PLACEHOLDERS.PHONE_NUMBER
                }}
              />
            ) : (
              <Input
                propsItem={{
                  name: 'email',
                  label: LABELS.EMAIL,
                  help: errors.email?.[0],
                  validateStatus: errors.email?.[0] && 'error',
                  validateTrigger: 'onChange',
                  rules: [
                    {
                      required: true,
                      type: 'email'
                    }
                  ]
                }}
                propsInput={{
                  placeholder: PLACEHOLDERS.EMAIL,
                  maxLength: 100
                }}
              />
            )}
          </div>
        </CustomForm>
      </div>
    </Popup>
  )
}
